/**
 * CR-6191: OTIF Review per-line actions — Override, Acknowledge, Reset.
 *
 * Each button renders only when the server says it applies (`can_override_otif`,
 * `can_acknowledge_otif`, `can_reset_otif` — admin + GRN score >= 100 + the
 * line's decision state). All three require a comment, which is sent to the
 * supplier as a message. Ports SP's OTIF Review actions. Override flips the
 * verdict to pass; Acknowledge records the fail as reviewed (verdict unchanged);
 * Reset clears either decision back to "Not Decided".
 */
import { useState } from 'react';
import { ShieldCheck, CheckCheck, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { purchasingAPI, PurchaseOrderLine } from '../../lib/api';
import { getApiErrorMessage } from '../../utils/errorUtils';

type OtifAction = 'override' | 'acknowledge' | 'reset';

const META: Record<OtifAction, { title: string; cta: string; success: string; description: string }> = {
  override: {
    title: 'Override OTIF Status',
    cta: 'Override',
    success: 'OTIF status overridden',
    description: 'Mark this fully-delivered line as OTIF-passed. A comment is required and is sent to the supplier.',
  },
  acknowledge: {
    title: 'Acknowledge OTIF Fail',
    cta: 'Acknowledge',
    success: 'OTIF status acknowledged',
    description: 'Record that this OTIF fail has been reviewed and accepted as-is. The verdict is unchanged. A comment is required.',
  },
  reset: {
    title: 'Reset OTIF Review',
    cta: 'Reset',
    success: 'OTIF review reset',
    description: 'Clear this line\'s OTIF review and return it to "Not Decided". A comment is required.',
  },
};

/** The slice of a PO line the actions need — lets PO Line Dashboard drill-down
 *  rows (which carry the same ``can_*`` flags) reuse this component. */
export type OTIFReviewActionTarget = Pick<
  PurchaseOrderLine, 'id' | 'can_override_otif' | 'can_acknowledge_otif' | 'can_reset_otif'
>;

interface OTIFReviewActionsProps {
  line: OTIFReviewActionTarget;
  /** Called after a successful action so the parent can refresh. */
  onDone: () => void;
  /** CR-6521: disabled while the table is in bulk-select mode. */
  disabled?: boolean;
  /**
   * CR-7418: `icon` renders the three actions as icon buttons, for the PO Lines
   * grid's Actions column, where every other control is an icon and three
   * labelled buttons would not fit. A VARIANT rather than a second component:
   * the dialog, the required comment and the copy are the parts that must not
   * drift between the two surfaces, and a copy of this file is how they would.
   */
  variant?: 'button' | 'icon';
}

/** Icon-button chrome, matching POLineRowActions' own controls. */
const ICON_BUTTON = 'p-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export default function OTIFReviewActions({
  line, onDone, disabled = false, variant = 'button',
}: OTIFReviewActionsProps) {
  const [action, setAction] = useState<OtifAction | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const close = () => { setAction(null); setComment(''); };

  const submit = async () => {
    if (!action) return;
    if (!comment.trim()) {
      toast.error('Please enter a comment/reason for this change.');
      return;
    }
    const data = { comments: comment.trim() };
    try {
      setSaving(true);
      if (action === 'override') await purchasingAPI.overrideOtif(line.id, data);
      else if (action === 'acknowledge') await purchasingAPI.acknowledgeOtif(line.id, data);
      else await purchasingAPI.resetOtif(line.id, data);
      toast.success(META[action].success);
      close();
      onDone();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update OTIF status'));
    } finally {
      setSaving(false);
    }
  };

  const meta = action ? META[action] : null;

  const dialog = (
    <Dialog open={action !== null} onOpenChange={(o) => { if (!saving && !o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{meta?.title}</DialogTitle>
          <DialogDescription>{meta?.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="otif-action-comment">Comment / reason</Label>
          <Textarea
            id="otif-action-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Reason for this change…"
            rows={4}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !comment.trim()}>
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {meta?.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (variant === 'icon') {
    // The colours are the DECISION's colours, not the control's: a control that
    // produces a state looks like the state it produces, on every surface
    // (Aaron, 19 Sep). Teal = Overridden, violet = Acknowledged. Reset is the
    // exception and stays slate rather than the amber its badge wears: amber
    // on a control reads as a warning, and the destination here is the ABSENCE
    // of a decision, not a state worth colouring. Outlined rather than filled: a solid button here is
    // the platform's primary/destructive pattern and a decision is neither.
    // The grid's other green tick already means "accept the order", which is
    // why Override is a SHIELD and not a tick.
    return (
      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
        {line.can_override_otif && (
          <button type="button" disabled={disabled} onClick={() => setAction('override')}
            className={`${ICON_BUTTON} hover:bg-teal-50`} title="Override OTIF — pass this line">
            <ShieldCheck className="h-5 w-5 text-teal-600" />
          </button>
        )}
        {line.can_acknowledge_otif && (
          <button type="button" disabled={disabled} onClick={() => setAction('acknowledge')}
            className={`${ICON_BUTTON} hover:bg-violet-50`} title="Acknowledge the OTIF fail — verdict unchanged">
            <CheckCheck className="h-5 w-5 text-violet-600" />
          </button>
        )}
        {line.can_reset_otif && (
          <button type="button" disabled={disabled} onClick={() => setAction('reset')}
            className={`${ICON_BUTTON} hover:bg-slate-100`} title="Reset the OTIF review to Not Decided">
            <RotateCcw className="h-5 w-5 text-slate-500" />
          </button>
        )}
        {dialog}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* CR-7418 round 3: the same two colours the badges use, so the modal's
          buttons, the grid's icons and the bulk bar all say the same thing. */}
      {line.can_override_otif && (
        <Button variant="outline" size="sm" disabled={disabled} onClick={() => setAction('override')}
          className="gap-1 border-teal-300 text-teal-800 hover:bg-teal-50 hover:text-teal-900">
          <ShieldCheck className="h-3.5 w-3.5" />Override
        </Button>
      )}
      {line.can_acknowledge_otif && (
        <Button variant="outline" size="sm" disabled={disabled} onClick={() => setAction('acknowledge')}
          className="gap-1 border-violet-300 text-violet-800 hover:bg-violet-50 hover:text-violet-900">
          <CheckCheck className="h-3.5 w-3.5" />Acknowledge
        </Button>
      )}
      {line.can_reset_otif && (
        <Button variant="ghost" size="sm" className="gap-1" disabled={disabled} onClick={() => setAction('reset')}>
          <RotateCcw className="h-3.5 w-3.5" />Reset
        </Button>
      )}

      {dialog}
    </div>
  );
}
